import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, DollarSign, Package, CheckCircle } from 'lucide-react';
import { productsService, ordersService } from '../services/api-service';
import { useAuth } from '../context/AuthContext';
import './ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      const productData = await productsService.getProductById(id);
      if (productData) {
        setProduct(productData);
      } else {
        setError('Product not found');
      }
    } catch (error) {
      console.error('Error loading product:', error);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      navigate('/?login=true');
      return;
    }

    if (product.status === 'out_of_stock') {
      alert('This product is currently out of stock.');
      return;
    }

    setProcessing(true);
    try {
      // Bypass external payment - test internal flow only
      const orderData = {
        items: [{
          productId: product.id,
          quantity: quantity,
          price: product.price
        }],
        total: product.price * quantity,
        // Payment bypassed for testing - mark as paid
        paymentStatus: 'paid',
        paymentMethod: 'test'
      };

      const response = await ordersService.createOrder(orderData);
      
      if (response.success || response.data) {
        setOrderSuccess(true);
        // Redirect to profile after 3 seconds
        setTimeout(() => {
          navigate('/profile');
        }, 3000);
      } else {
        throw new Error('Order creation failed');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert(`Purchase failed: ${error.message || 'Please try again.'}`);
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="product-details-page">
        <div className="container">
          <div className="product-details-loading">
            <ShoppingBag size={64} className="spinning" />
            <h3>Loading product details...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-details-page">
        <div className="container">
          <div className="product-details-error">
            <h2>Product Not Found</h2>
            <p>{error || 'The product you are looking for does not exist.'}</p>
            <Link to="/products" className="btn">
              <ArrowLeft size={20} />
              Back to Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const total = product.price * quantity;
  const isOutOfStock = product.status === 'out_of_stock';

  return (
    <div className="product-details-page">
      {/* Header with Back Button */}
      <section className="product-details-header">
        <div className="container">
          <Link to="/products" className="product-details-back">
            <ArrowLeft size={20} />
            Back to Products
          </Link>
        </div>
      </section>

      {/* Product Content */}
      <section className="product-details-content section">
        <div className="container">
          <div className="product-details-grid">
            {/* Product Image */}
            <div className="product-details-image-section">
              {product.image_url ? (
                <div className="product-details-image">
                  <img src={product.image_url} alt={product.name} />
                  {isOutOfStock && (
                    <div className="product-details-badge product-details-badge-out-of-stock">
                      Out of Stock
                    </div>
                  )}
                </div>
              ) : (
                <div className="product-details-image-placeholder">
                  <Package size={64} />
                  <p>No image available</p>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="product-details-info">
              <div className="product-details-header-info">
                {product.category && (
                  <span className="product-details-category">{product.category}</span>
                )}
                <h1>{product.name}</h1>
                <div className="product-details-price">
                  <DollarSign size={28} />
                  <span>{formatPrice(product.price)}</span>
                </div>
              </div>

              {product.description && (
                <div className="product-details-description">
                  <h2>Description</h2>
                  <p>{product.description}</p>
                </div>
              )}

              {/* Purchase Section */}
              {orderSuccess ? (
                <div className="product-details-success">
                  <CheckCircle size={48} />
                  <h3>Order Successful!</h3>
                  <p>Your order has been placed. Redirecting to your profile...</p>
                </div>
              ) : (
                <div className="product-details-purchase">
                  <h2>Purchase</h2>
                  
                  {!isOutOfStock && (
                    <div className="product-details-quantity">
                      <label htmlFor="quantity">Quantity:</label>
                      <div className="product-details-quantity-controls">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          disabled={quantity <= 1}
                          className="product-details-quantity-btn"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          id="quantity"
                          min="1"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="product-details-quantity-input"
                        />
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="product-details-quantity-btn"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="product-details-total">
                    <span>Total:</span>
                    <span className="product-details-total-amount">
                      {formatPrice(total)}
                    </span>
                  </div>

                  {user ? (
                    <button
                      onClick={handlePurchase}
                      disabled={processing || isOutOfStock}
                      className="btn btn-orange btn-block"
                    >
                      {processing ? (
                        'Processing...'
                      ) : isOutOfStock ? (
                        'Out of Stock'
                      ) : (
                        <>
                          <ShoppingBag size={20} />
                          Purchase Now
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/?login=true')}
                      className="btn btn-orange btn-block"
                    >
                      Login to Purchase
                    </button>
                  )}

                  <p className="product-details-note">
                    * Payment processing bypassed for testing. Order will be created directly.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetails;


